/**
 * ReportsPage
 * Página para visualizar reportes de PowerBI embebidos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FaBookmark } from 'react-icons/fa';
import { getAvailableReports, getEmbedToken } from '../../services/powerbiService';
import { useTranslation } from '../../i18n';
import BookmarkManager from '../../components/powerbi/BookmarkManager';
import useActivityTracker from '../../hooks/useActivityTracker';
import './ReportsPage.css';

const ReportsPage = ({
  availableReports,
  currentReportId,
  currentPage,
  onReportDataLoaded,
  onPageChange
}) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackAction } = useActivityTracker(user?.username, 'reports');

  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [isReportReady, setIsReportReady] = useState(false);
  const [lastRefreshDate, setLastRefreshDate] = useState(null);
  const [isBookmarkManagerOpen, setIsBookmarkManagerOpen] = useState(false);

  // Retry states
  const [retrying, setRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState('');

  // Cargar token de embed cuando cambia el reporte
  useEffect(() => {
    if (user?.username && currentReportId) {
      loadEmbedToken();
    }
  }, [user?.username, currentReportId]);

  // Cambiar página cuando el reporte esté listo
  useEffect(() => {
    if (isReportReady && report && currentPage) {
      changePage(currentPage);
    }
  }, [isReportReady, currentPage]);

  // Ocultar banner de PowerBI cuando el reporte está listo
  useEffect(() => {
    if (isReportReady) {
      const hideBanner = () => {
        const banner = document.querySelector('notification-bar, [data-testid="notification-bar"]');
        if (banner) {
          banner.style.display = 'none';
          banner.style.visibility = 'hidden';
          banner.style.height = '0';
          banner.style.overflow = 'hidden';
        }
      };

      hideBanner();
      setTimeout(hideBanner, 1000);
      setTimeout(hideBanner, 3000);
    }
  }, [isReportReady]);

  // Función para cargar el token de embed
  const loadEmbedToken = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsReportReady(false);
      setReport(null);

      // Buscar el nombre del reporte desde availableReports
      const currentReport = availableReports?.find(r => r.id === currentReportId);
      const reportName = currentReport?.name || currentReport?.displayName || null;

      const data = await getEmbedToken(user.username, currentReportId, reportName);

      // Trackear cambio de reporte
      trackAction('view_report', { report_id: currentReportId, report_name: reportName });

      // Guardar fecha de última actualización
      if (data.lastRefreshDate) {
        setLastRefreshDate(data.lastRefreshDate);
      }

      // Notificar al padre los datos cargados
      if (onReportDataLoaded) {
        onReportDataLoaded({
          menu: data.menu,
          lastRefreshDate: data.lastRefreshDate,
          organization: data.organization,
          role: data.role,
          features: data.features
        });
      }

      setEmbedConfig({
        type: 'report',
        id: data.reportId,
        embedUrl: data.embedUrl,
        accessToken: data.accessToken,
        tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: {
              expanded: false,
              visible: false
            },
            pageNavigation: {
              visible: false
            }
          },
          background: models.BackgroundType.Transparent,
          layoutType: models.LayoutType.Custom,
          customLayout: {
            displayOption: models.DisplayOption.FitToWidth
          }
        }
      });

      setLoading(false);
    } catch (err) {
      console.error('[ReportsPage] Error loading embed token:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Función para cambiar de página dentro del reporte
  const changePage = useCallback(async (pageName) => {
    if (!report) return;

    try {
      const pages = await report.getPages();
      const targetPage = pages.find(page => page.displayName === pageName);

      if (targetPage) {
        await targetPage.setActive();
        trackAction('view_report_page', { report_id: currentReportId, page_name: pageName });
        if (onPageChange) {
          onPageChange(pageName);
        }
      } else {
        console.warn('[ReportsPage] Page not found:', pageName);
      }
    } catch (err) {
      console.error('[ReportsPage] Error changing page:', err);
    }
  }, [report, onPageChange]);

  // Función para reintentar carga
  const handleRetry = () => {
    loadEmbedToken();
  };

  // Función para exportar a PDF
  const handleExportPDF = async () => {
    if (!report) return;

    try {
      trackAction('export_pdf', { report_id: currentReportId, page_name: currentPage });
      await report.print();
    } catch (err) {
      console.error('[ReportsPage] Error exporting PDF:', err);
    }
  };

  // Función para resetear filtros
  const handleResetFilters = () => {
    trackAction('reset_filters', { report_id: currentReportId });
    window.location.reload();
  };

  // Event handlers para PowerBIEmbed
  const eventHandlers = new Map([
    ['loaded', () => {
      console.log('[ReportsPage] Report loaded');
    }],
    ['rendered', () => {
      console.log('[ReportsPage] Report rendered');
      setIsReportReady(true);
    }],
    ['error', (event) => {
      const errorDetail = event.detail;
      const errorMessage = errorDetail?.message || '';
      const errorLevel = errorDetail?.level;

      // Lista de errores que NO son críticos
      const nonCriticalErrors = [
        'visualClickedFailed',
        'visualClicked',
        'dataSelected',
        'commandTriggered'
      ];

      if (nonCriticalErrors.includes(errorMessage)) {
        console.warn('[ReportsPage] Non-critical event:', errorMessage);
        return;
      }

      if (errorLevel && errorLevel <= 2) {
        console.warn('[ReportsPage] Warning:', errorDetail);
        return;
      }

      console.error('[ReportsPage] Error:', errorDetail);
    }]
  ]);

  // Callback para obtener referencia al reporte
  const getEmbeddedComponent = (embeddedReport) => {
    setReport(embeddedReport);
  };

  // Formatear fecha de última actualización
  const formatLastRefresh = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="reports-page">
        <div className="reports-loading-state">
          <div className="reports-spinner"></div>
          <h3>{retrying ? 'Reintentando...' : 'Cargando dashboard...'}</h3>
          <p>{retrying ? retryMessage : 'Obteniendo credenciales de Power BI'}</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="reports-page">
        <div className="reports-error-state">
          <div className="reports-error-icon">⚠️</div>
          <h3>Error al cargar el reporte</h3>
          <p>{error}</p>
          <div className="reports-error-actions">
            <button className="reports-retry-btn" onClick={handleRetry}>
              🔄 Reintentar
            </button>
            <button className="reports-back-btn" onClick={() => navigate('/')}>
              ← Volver al chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render no report selected
  if (!currentReportId) {
    return (
      <div className="reports-page">
        <div className="reports-empty-state">
          <div className="reports-empty-icon">📊</div>
          <h3>Selecciona un reporte</h3>
          <p>Elige un reporte del menú lateral para visualizarlo</p>
        </div>
      </div>
    );
  }

  // Render PowerBI embed
  return (
    <div className="reports-page">
      {/* TopBar con controles */}
      <div className="reports-topbar">
        <div className="reports-topbar-left">
          <h2 className="reports-title">Balance Score Card Digital</h2>
          {lastRefreshDate && (
            <div className="reports-last-refresh">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              <span>Actualizado: {formatLastRefresh(lastRefreshDate)}</span>
            </div>
          )}
        </div>

        <div className="reports-topbar-right">
          <button
            className="reports-topbar-btn reports-bookmarks-btn"
            onClick={() => { trackAction('open_bookmark_manager', { report_id: currentReportId }); setIsBookmarkManagerOpen(true); }}
            title={t('reports.myFilters')}
          >
            <FaBookmark />
            <span>{t('reports.myFilters')}</span>
          </button>

          <button className="reports-topbar-btn" onClick={handleExportPDF} title={t('reports.exportPdf')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="12" y2="18" />
              <line x1="15" y1="15" x2="12" y2="18" />
            </svg>
            <span>{t('reports.exportPdf')}</span>
          </button>

          <button className="reports-topbar-btn" onClick={handleResetFilters} title={t('reports.resetFilters')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            <span>{t('reports.resetFilters')}</span>
          </button>
        </div>
      </div>

      {/* PowerBI Container */}
      {embedConfig && (
        <div className="reports-powerbi-container">
          <PowerBIEmbed
            embedConfig={embedConfig}
            eventHandlers={eventHandlers}
            getEmbeddedComponent={getEmbeddedComponent}
            cssClassName="reports-powerbi-embed"
          />
        </div>
      )}

      {/* Bookmark Manager Modal */}
      <BookmarkManager
        report={report}
        username={user.username}
        reportId={currentReportId}
        isVisible={isBookmarkManagerOpen}
        onClose={() => setIsBookmarkManagerOpen(false)}
      />
    </div>
  );
};

export default ReportsPage;
